// World state broadcast from server to client.
//
// Broadcast at ~15 Hz (half server tick rate, 30 Hz). The client interpolates
// between consecutive snapshots for smoothness. Bouncers are static — sent
// once in the `welcome` message, not in snapshots.
//
// Wire shapes are plain JSON (no Maps): a snapshot serializes players as an
// array. `toSnapshot` / players-by-id reconstruction live here so both ends
// agree on the shape.

import type { PlayerId, Player } from '../entities/player.js';
import type { Pellet } from '../entities/pellet.js';
import type { WorldState } from '../sim/tick.js';

/** Per-player wire record. A subset of Player — no internal tick counters. */
export interface SnapshotPlayer {
  id: PlayerId;
  name: string;
  color: string;
  edgeId: number;
  t: number;
  direction: 'forward' | 'backward';
  dirPref: 'left' | 'right';
  speed: number;
  /** Fuel tank (0..tuning.maxFuel) — HUD meter. */
  fuel: number;
  health: number;
  /** True while respawn invulnerability is active (client: pulsing ring). */
  invuln: boolean;
  /** Queued future turns — the local client draws its trail from this. */
  pendingTurns: ('left' | 'right')[];
}

export interface WorldSnapshot {
  tick: number;
  players: SnapshotPlayer[];
  pellets: Pellet[];
}

/** Build the wire snapshot from authoritative state. */
export function toSnapshot(state: WorldState): WorldSnapshot {
  const players: SnapshotPlayer[] = [];
  for (const p of state.players.values()) {
    players.push({
      id: p.id,
      name: p.name,
      color: p.color,
      edgeId: p.edgeId,
      t: p.t,
      direction: p.direction,
      dirPref: p.dirPref,
      speed: p.speed,
      fuel: p.fuel,
      health: p.health,
      invuln: p.invulnUntilTick > state.tick,
      pendingTurns: p.pendingTurns,
    });
  }
  return { tick: state.tick, players, pellets: state.pellets };
}

/** Index snapshot players by id (client-side convenience). */
export function snapshotPlayersById(s: WorldSnapshot): Map<PlayerId, SnapshotPlayer> {
  const m = new Map<PlayerId, SnapshotPlayer>();
  for (const p of s.players) m.set(p.id, p);
  return m;
}
